<template>
    <v-app id="k2" dark>
        <main>
            <v-main>

                <v-dialog persistent transition="scale-transition" max-width="450" v-model="showSuccessDialog">

                    <!-- CONFIRMATION CODE HAS EXPIRED -->
                    <v-card>
                        <v-toolbar dense color="grey darken-4" flat>
                            <v-toolbar-title>Registration success!</v-toolbar-title>
                            <v-spacer></v-spacer>
                        </v-toolbar>

                        <v-card-text class="pt-5">
                            You have been successfully registered! Click on the login button
                            below to start your journey!
                        </v-card-text>

                        <v-card-actions>
                            <v-btn @click="goToLogin" color="primary">Login</v-btn>
                        </v-card-actions>

                    </v-card>
                </v-dialog>

                <v-dialog persistent transition="scale-transition" max-width="650" v-model="showFormDialog">

                    <v-form v-model="formValid">

                        <!-- CONFIRMATION CODE HAS EXPIRED -->
                        <v-card v-if="invitationCodeStatus==='expired'">
                            <v-toolbar dense color="grey darken-4" flat>
                                <v-toolbar-title>Invitation expired!</v-toolbar-title>
                                <v-spacer></v-spacer>
                            </v-toolbar>

                            <v-card-text class="pt-5">
                                Your invitation to join the team has expired. You have 24 hours to accept the
                                invitation. Please contact the person who invited you to join and ask them resend you a
                                new
                                invitation.
                            </v-card-text>
                        </v-card>

                        <!-- CONFIRMATION CODE HAS BEEN USED -->
                        <v-card v-if="invitationCodeStatus==='emailexists'">
                            <v-toolbar dense color="grey darken-4" flat>
                                <v-toolbar-title>Already signed up!</v-toolbar-title>
                                <v-spacer></v-spacer>
                            </v-toolbar>

                            <v-card-text class="pt-5">
                                <v-alert type="info" class="body-2">
                                    It appears that you have already signed up! Please go to login.
                                </v-alert>
                            </v-card-text>

                            <v-card-actions>
                                <v-btn @click="goToLogin" color="primary">Go to login</v-btn>
                            </v-card-actions>

                        </v-card>

                        <!-- IF CONFIRMATION CODE DOES NOT MATCH ANYTHING -->
                        <v-card v-if="invitationCodeStatus==='invalid'">

                            <v-toolbar dense color="grey darken-4" flat>
                                <v-toolbar-title>Invalid invitation code</v-toolbar-title>
                                <v-spacer></v-spacer>
                            </v-toolbar>

                            <v-card-text class="pt-5">
                                <v-alert type="error" icon="mdi-alert" class="body-2" prominent>
                                    There was a problem with your invitation. If you manually pasted the link into your
                                    browser's address bar, please verify that you pasted the entire URL.
                                </v-alert>
                            </v-card-text>
                        </v-card>

                        <v-card v-if="invitationCodeStatus==='valid'">
                            <v-toolbar dense color="grey darken-4" flat>
                                <v-toolbar-title>Welcome {{ this.principal }}!</v-toolbar-title>
                                <v-spacer></v-spacer>
                            </v-toolbar>

                            <v-card-text class="pt-5">

                                <h3>Great you are in!</h3>

                                <p class="pt-4">
                                    Let's setup your mobile phone number, timezone and password. Your password should be
                                    at least 7 characters long and contain combination of upper and lower case letters,
                                    numbers and special characters. Your mobile phone number is required for two factor
                                    authentication.
                                </p>

                                <cell-field
                                    :country-code="form.data.country_code"
                                    :phone-number="form.data.cell"
                                    :error-messages="form.getError('cell')"
                                    @country-code-selected="countryCodeSelected"
                                    @phone-number-input="phoneNumberInput"
                                    :error-messages-country-code="form.getError('country_code')"
                                    :error-messages-phone-number="form.getError('cell')"
                                />

                                <v-select
                                    prepend-icon="mdi-earth"
                                    label="Timezone"
                                    :items="timezones"
                                    v-model="form.data.timezone"
                                    item-value="text"
                                    required
                                    :rules="[v => !!v || 'You must select timezone!']"
                                    :error-messages="form.getError('timezone')"
                                ></v-select>

                                <password-field
                                    v-model="form.data.password"
                                    label="New password"
                                    label-confirm="Confirm password"
                                    :error-messages="form.getError('password')"
                                />

                            </v-card-text>

                            <v-card-actions>
                                <v-btn class="ml-2 mb-2"
                                       color="primary"
                                       @click="processContinue"
                                       :disabled="!formValid">Continue
                                </v-btn>

                            </v-card-actions>
                        </v-card>

                    </v-form>

                </v-dialog>
            </v-main>

        </main>
    </v-app>

</template>

<script>

import Store from '../store';
import {mapActions, mapState} from 'vuex';
import PasswordField from '../../../components/UI/PasswordField';
import CellPhoneField from '../../../components/UI/CellPhoneField';
import Form from '../../../components/forms/Form';
import moment from 'moment-timezone';
import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';

export default {

    mixins: [timeDisplay],

    components: {
        'password-field': PasswordField,
        'cell-field': CellPhoneField
    },

    data() {
        return {

            // Create password and cell form
            form: new Form({
                password: null,
                cell: '',
                country_code: null,
                timezone: '',
                timeFormat: '24h',
                dateFormat: 'YYYY-MM-DD',
                token: this.token
            }),
            formValid: false,

            invitationCodeStatus: null,

            showSuccessDialog: false,
            showFormDialog: false,

            principal: false,
        }
    },

    computed: {
        // Global state
        ...mapState(['timezones', 'timezonesLoading']),

    },

    created() {
        if (!this.$store.state.AccountManager) {
            this.$store.registerModule('Join', Store);
        }

        // Check if token is good
        axios.post('/join/token', {'token': this.$route.params.token})
            .catch(error => {
                this.showFormDialog = true;
            })
            .then(({data}) => {
                this.showFormDialog = true;
                this.invitationCodeStatus = data.status;
                this.form.data.token = data.token;
                this.principal = data.principal;
            });

        this.loadTimezones()
            .catch(error => {

            })
            .then(() => {
                this.processAutoSelects();
            });

    },

    beforeDestroy() {
        this.$store.unregisterModule('Join');
    },

    methods: {
        // Global actions
        ...mapActions(['loadTimezones']),

        goToLogin() {
            window.location.replace('/');
        },

        // User clicks on continue
        processContinue() {

            axios.post('/join/save', this.form.data)
                .catch((error) => {

                    console.log(error);

                    // Form errors
                    if (error.response.status === 422) {
                        this.form.setErrors(error.response.data.errors);
                    } else if (error.response.status === 409) {
                        Bus.$emit('flash-message', {
                            type: 'warning',
                            message: 'There is a problem with your invitation status: ' + error.response.data.status
                        });
                    } else {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Something went wrong while saving...'
                        });
                    }
                })
                .then(() => {
                    this.showFormDialog = false;
                    this.showSuccessDialog = true;
                });
        },

        countryCodeSelected(value) {
            this.form.data.country_code = value;
        },

        phoneNumberInput(value) {
            this.form.data.cell = value;
        },

        processAutoSelects() {
            this.form.data.timezone = moment.tz.guess();

            // Select the stupid American standard automatically
            if (this.form.data.timezone.includes('America')) {
                this.form.data.dateFormat = 'MM/DD/YYYY';
                this.form.data.timeFormat = '12h';
            }
        }
    }
}
</script>
